import type { Audience } from './auth.types';

// ─── Audit action enum ─────────────────────────────────────────────────────────

export type AuditAction =
  // Auth
  | 'login'
  | 'logout'
  // Content
  | 'content_created'
  | 'content_updated'
  | 'content_deleted'
  | 'content_status_changed'
  | 'content_published'
  | 'content_copied'
  // Media
  | 'image_uploaded'
  | 'image_replaced'
  | 'image_removed'
  | 'audio_uploaded'
  | 'audio_replaced'
  | 'audio_removed'
  // User management
  | 'user_registered'
  | 'user_approved'
  | 'user_rejected'
  | 'user_activated'
  | 'user_deactivated'
  // Permissions
  | 'permissions_changed'
  | 'role_changed';

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  login:                 'Login',
  logout:                'Logout',
  content_created:       'Content Created',
  content_updated:       'Content Updated',
  content_deleted:       'Content Deleted',
  content_status_changed:'Status Changed',
  content_published:     'Content Published',
  content_copied:        'Content Copied',
  image_uploaded:        'Image Uploaded',
  image_replaced:        'Image Replaced',
  image_removed:         'Image Removed',
  audio_uploaded:        'Audio Uploaded',
  audio_replaced:        'Audio Replaced',
  audio_removed:         'Audio Removed',
  user_registered:       'User Registered',
  user_approved:         'User Approved',
  user_rejected:         'User Rejected',
  user_activated:        'User Activated',
  user_deactivated:      'User Deactivated',
  permissions_changed:   'Permissions Changed',
  role_changed:          'Role Changed',
};

/** Groups for display in the audit log filter. */
export const AUDIT_ACTION_GROUPS: Record<string, AuditAction[]> = {
  'Authentication': ['login', 'logout'],
  'Content':        ['content_created', 'content_updated', 'content_deleted',
                     'content_status_changed', 'content_published', 'content_copied'],
  'Media':          ['image_uploaded', 'image_replaced', 'image_removed',
                     'audio_uploaded', 'audio_replaced', 'audio_removed'],
  'User Management':['user_registered', 'user_approved', 'user_rejected',
                     'user_activated', 'user_deactivated'],
  'Permissions':    ['permissions_changed', 'role_changed'],
};

// ─── Audit log entry ───────────────────────────────────────────────────────────

export interface AuditLog {
  id:              string;
  userId:          string;
  userDisplayName: string;
  userEmail:       string;
  action:          AuditAction;
  timestamp:       Date;

  // Content context (nullable for non-content actions)
  audience:    Audience | null;
  monthNumber: number | null;
  weekNumber:  number | null;
  dayNumber:   number | null;
  recordId:    string | null;

  // Change detail
  fieldsChanged:   string[];
  previousValues:  Record<string, unknown>;
  newValues:       Record<string, unknown>;

  // Flexible metadata
  metadata: Record<string, unknown>;
}

/** Input used when writing a new audit log entry. */
export type AuditLogInput = Omit<AuditLog, 'id' | 'timestamp'>;

// ─── Filters ───────────────────────────────────────────────────────────────────

export interface AuditLogFilters {
  action?:    AuditAction | null;
  audience?:  Audience | null;
  userId?:    string | null;
  dateFrom?:  Date | null;
  dateTo?:    Date | null;
}
